import { formatDobMdY, isoDateOnlyFromDob } from "../utils/dob-display.util";

export interface ParsedPatientHeader {
  name?: string;
  dob?: string;
  dos?: string;
  provider?: string;
}

/** Patient header embedded in SOAP/signable HTML. */
export function parsePatientHeaderFromHtml(
  html: string
): ParsedPatientHeader | null {
  if (!html?.trim()) {
    return null;
  }
  try {
    const tempDiv = window.document.createElement("div");
    tempDiv.innerHTML = html;
    const header = tempDiv.querySelector(".editor-custom-header");
    if (!header) {
      return null;
    }
    const text = header.textContent || "";
    const pick = (label: string): string | undefined => {
      const re = new RegExp(`${label}\\s*:\\s*([^\\n]+)`, "i");
      const m = text.match(re);
      return m?.[1]?.trim() || undefined;
    };
    return {
      name: pick("Patient"),
      dob: pick("DOB"),
      dos: pick("DOS"),
      provider: pick("Provider"),
    };
  } catch {
    return null;
  }
}

/** Normalize a calendar date to `YYYY-MM-DD` for storage and pipes. */
export function toCalendarIsoDate(
  value: string | null | undefined
): string | null {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return null;
  }
  const iso = isoDateOnlyFromDob(raw);
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return iso;
  }
  const display = formatDobMdY(raw, true);
  if (!display) {
    return null;
  }
  const parts = display.split("/");
  if (parts.length !== 3) {
    return null;
  }
  return `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}`;
}

/**
 * Prefer API `scheduleDate`, then DOS embedded in signable HTML, then fallback.
 */
export function resolveSignableDocumentDos(
  scheduleDate: string | null | undefined,
  content: string | null | undefined,
  fallbackDos: string | null | undefined
): string | null {
  const fromSchedule = toCalendarIsoDate(scheduleDate);
  if (fromSchedule) {
    return fromSchedule;
  }
  const parsedDos = content
    ? parsePatientHeaderFromHtml(content)?.dos
    : undefined;
  return toCalendarIsoDate(parsedDos) || toCalendarIsoDate(fallbackDos);
}

/** Format DOS for PDF/list display (`MM/DD/YYYY`). */
export function formatHeaderDosForDisplay(
  value: string | null | undefined
): string | null {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return null;
  }
  return formatDobMdY(raw, true) || raw;
}

function normalizeSoapNoteTypeToken(
  value: string | null | undefined
): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

/** True when a signables-with-documents row represents the encounter SOAP note. */
export function isSoapNoteSignableType(
  type: string | null | undefined,
  signableType?: string | null | undefined
): boolean {
  const normalizedType = normalizeSoapNoteTypeToken(type);
  const normalizedSignableType = normalizeSoapNoteTypeToken(signableType);
  return (
    normalizedType === "soap_note" ||
    normalizedSignableType === "soap_note"
  );
}

export const SOAP_NOTE_DOCUMENT_TYPE_DISPLAY = "Encounter Note";

function normalizeDocumentTypeToken(
  value: string | null | undefined
): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

/** True when a stored/API document type label refers to the encounter SOAP note. */
export function isSoapNoteDocumentTypeLabel(
  documentType: string | null | undefined
): boolean {
  const normalized = normalizeDocumentTypeToken(documentType);
  return (
    normalized === "soap_note" ||
    normalized === "soap" ||
    normalized === "encounter_note" ||
    normalized === "encounter"
  );
}

/** True when a signables-with-documents row represents a procedure room protocol. */
export function isProcedureRoomProtocolSignableType(
  type: string | null | undefined,
  signableType?: string | null | undefined
): boolean {
  const normalizedType = normalizeDocumentTypeToken(type);
  const normalizedSignableType = normalizeDocumentTypeToken(signableType);
  return (
    normalizedType === "procedureroom_protocol" ||
    normalizedSignableType === "procedureroom_protocol"
  );
}

export const PROCEDURE_ROOM_PROTOCOL_DOCUMENT_TYPE_DISPLAY = "Order";

/**
 * Plan / clinical order signables (`Type_LB93ke4v26_Order` in DB;
 * `Type LB93ke4v26 Order` after API formatting).
 */
export function isPlanOrderDocumentTypeLabel(
  documentType: string | null | undefined
): boolean {
  const trimmed = String(documentType ?? "").trim();
  if (!trimmed) {
    return false;
  }
  const lower = trimmed.toLowerCase();
  if (lower === "soap_note" || lower === "soapnote" || lower === "soap note") {
    return false;
  }
  if (lower.includes("work order")) {
    return false;
  }
  if (normalizeDocumentTypeToken(trimmed) === "orders_order") {
    return true;
  }
  const underscored = trimmed.replace(/\s+/g, "_");
  if (/_.+_Order$/i.test(underscored)) {
    return true;
  }
  return /\s.+\sOrder$/i.test(trimmed) || /^.+\sOrder$/i.test(trimmed);
}

/** True when signables-with-documents metadata refers to a plan clinical order. */
export function isPlanOrderSignableType(
  type: string | null | undefined,
  signableType?: string | null | undefined
): boolean {
  return [type, signableType].some((value) =>
    isPlanOrderDocumentTypeLabel(value)
  );
}

/** True when a stored/API document type label refers to a procedure room protocol. */
export function isProcedureRoomProtocolDocumentTypeLabel(
  documentType: string | null | undefined
): boolean {
  const normalized = normalizeDocumentTypeToken(documentType);
  return (
    normalized === "procedureroom_protocol" ||
    normalized === "order" ||
    normalized === "orders"
  );
}

/** UI label for signable rows in document lists (e.g. Global Documents). */
export function resolveSignableDocumentTypeDisplay(
  documentType: string | null | undefined,
  type?: string | null | undefined,
  signableType?: string | null | undefined
): string {
  if (
    isSoapNoteSignableType(type, signableType) ||
    isSoapNoteDocumentTypeLabel(documentType)
  ) {
    return SOAP_NOTE_DOCUMENT_TYPE_DISPLAY;
  }
  if (
    isProcedureRoomProtocolSignableType(type, signableType) ||
    isProcedureRoomProtocolDocumentTypeLabel(documentType)
  ) {
    return PROCEDURE_ROOM_PROTOCOL_DOCUMENT_TYPE_DISPLAY;
  }
  if (
    isPlanOrderSignableType(type, signableType) ||
    isPlanOrderDocumentTypeLabel(documentType)
  ) {
    return PROCEDURE_ROOM_PROTOCOL_DOCUMENT_TYPE_DISPLAY;
  }
  return String(documentType ?? "").trim();
}

/** @deprecated Prefer {@link resolveSignableDocumentTypeDisplay}. */
export function resolveSoapNoteDocumentTypeDisplay(
  documentType: string | null | undefined,
  type?: string | null | undefined,
  signableType?: string | null | undefined
): string {
  return resolveSignableDocumentTypeDisplay(documentType, type, signableType);
}

const SERVICE_ID_IN_SIGNABLE_TITLE_REGEX =
  /\(([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)/i;

const PROBABLE_UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isProbableUuid(value: string | null | undefined): boolean {
  return PROBABLE_UUID_REGEX.test(String(value ?? "").trim());
}

function firstNonEmptyServiceSource(
  ...values: Array<string | null | undefined>
): string {
  for (const value of values) {
    const trimmed = String(value ?? "").trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return "";
}

/** Service id embedded in signable title/displayName, e.g. `Patient - (uuid) 06/04/2026`. */
export function extractServiceIdFromSignableTitle(
  title: string | null | undefined
): string | null {
  const match = String(title ?? "").match(SERVICE_ID_IN_SIGNABLE_TITLE_REGEX);
  return match?.[1] ?? null;
}

/** Replace `(service-uuid)` in a signable title with the resolved service name. */
export function replaceEmbeddedServiceIdInSignableTitle(
  title: string | null | undefined,
  serviceName: string | null | undefined
): string {
  const raw = String(title ?? "").trim();
  const service = String(serviceName ?? "").trim();
  if (!raw || !service) {
    return raw;
  }
  return raw.replace(SERVICE_ID_IN_SIGNABLE_TITLE_REGEX, service);
}

/** `Patient Name - Service Name DOS` for SOAP note rows in document lists. */
export function buildSoapNoteSignableDisplayTitle(input: {
  patientName?: string | null;
  serviceName?: string | null;
  dos?: string | null;
}): string {
  const patient = String(input.patientName ?? "").trim();
  const service = String(input.serviceName ?? "").trim();
  const dos =
    formatHeaderDosForDisplay(input.dos) || String(input.dos ?? "").trim();
  const serviceDos = [service, dos].filter(Boolean).join(" ");

  if (patient && serviceDos) {
    return `${patient} - ${serviceDos}`;
  }
  if (patient) {
    return patient;
  }
  return serviceDos;
}

/** Resolve SOAP note title/displayName, replacing embedded service ids when possible. */
export function resolveSoapNoteSignableDisplayTitle(input: {
  title?: string | null;
  displayName?: string | null;
  patientName?: string | null;
  serviceNameOrId?: string | null;
  appointmentService?: string | null;
  dos?: string | null;
  resolveServiceDisplayName: (serviceNameOrId: string) => string;
}): string {
  const titleOrDisplay = String(
    input.displayName ?? input.title ?? ""
  ).trim();
  const serviceIdFromTitle = extractServiceIdFromSignableTitle(titleOrDisplay);
  const serviceRaw = firstNonEmptyServiceSource(
    input.serviceNameOrId,
    serviceIdFromTitle,
    input.appointmentService
  );
  const resolvedServiceName = serviceRaw
    ? input.resolveServiceDisplayName(serviceRaw)
    : "";
  const serviceName =
    resolvedServiceName && !isProbableUuid(resolvedServiceName)
      ? resolvedServiceName
      : "";

  if (serviceIdFromTitle && serviceName && titleOrDisplay) {
    return replaceEmbeddedServiceIdInSignableTitle(titleOrDisplay, serviceName);
  }

  const built = buildSoapNoteSignableDisplayTitle({
    patientName: input.patientName,
    serviceName,
    dos: input.dos,
  });
  if (built) {
    return built;
  }

  return titleOrDisplay;
}
